import QRCode from 'qrcode';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { main } from '../lib/text';
import type { Settings as S, Station } from '../lib/types';
import { navigate } from '../lib/router';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

/** /settings (owner): PromptPay, kitchen stations, takeout link, self-seating. */
export function Settings({ path }: { path: string }) {
  const [settings, setSettings] = useState<S | null>(null);
  const [promptpay, setPromptpay] = useState('');
  const [stations, setStations] = useState<Station[]>([]);
  const [link, setLink] = useState<{ url: string; svg: string } | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const { s, online } = useStaff(() => undefined, () => undefined);
  const token = s?.token ?? '';

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [r, l] = await Promise.all([
        api<{ settings: S }>('GET', '/restaurant', { token }),
        api<{ url: string }>('GET', '/takeout/link', { token }),
      ]);
      setSettings(r.settings);
      setPromptpay(r.settings.promptpayId ?? '');
      setStations(r.settings.stations);
      setLink({ url: l.url, svg: await QRCode.toString(l.url, { type: 'svg', margin: 1 }) });
    } catch (e) { handleAuthError(e); setMsg({ ok: false, text: (e as Error).message }); }
  }, [token]);
  useEffect(() => { void load(); }, [load]);

  const save = async (body: Record<string, unknown>, ok = 'บันทึกแล้ว · Saved') => {
    setMsg(null);
    try { setSettings(await api<S>('PUT', '/restaurant/settings', { token, body })); setMsg({ ok: true, text: ok }); }
    catch (e) { handleAuthError(e); setMsg({ ok: false, text: (e as Error).message }); }
  };

  return (
    <Shell path={path} online={online} sub="settings">
      {msg && <div className={msg.ok ? 'ticket' : 'warn'} role="status" style={{ color: 'var(--ink)', marginBottom: 12 }}>{msg.text}</div>}
      {settings && (
        <div className="tiles" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', alignItems: 'start' }}>
          <section className="kt" style={{ gap: 10 }}>
            <h2 className="display" style={{ margin: 0, fontSize: 20 }}>หน้าร้านในเมือง · Town storefront</h2>
            <p style={{ margin: 0, fontSize: 14 }}>ออกแบบหน้าร้าน 3D เลือกประเภท (ร้าน ริมทาง ตลาด ฟู้ดทรัค) ตั้งระยะส่ง และเปิดให้ลูกค้าเดินเจอในเมืองเฟิร์สแมงโก้ · Design your 3D storefront, pick shop / street stall / market stall / food truck, set delivery and list your shop in FirstMango Town.</p>
            <button className="cta light" onClick={() => navigate('/storefront')}>ออกแบบหน้าร้าน · Design storefront</button>
          </section>

          <section className="kt" style={{ gap: 10 }}>
            <h2 className="display" style={{ margin: 0, fontSize: 20 }}>พร้อมเพย์ · PromptPay</h2>
            <p style={{ margin: 0, fontSize: 14 }}>เงินเข้าบัญชีร้านโดยตรง · Payments go straight to your own account. Mobile number, 13-digit ID / tax ID, or e-wallet ID.</p>
            <label className="field">PromptPay ID<input value={promptpay} onChange={(e) => setPromptpay(e.target.value)} placeholder="081-234-5678" inputMode="numeric" /></label>
            <button className="cta light" onClick={() => void save({ promptpayId: promptpay.trim() || null })}>บันทึก · Save</button>
          </section>

          <section className="kt" style={{ gap: 10 }}>
            <h2 className="display" style={{ margin: 0, fontSize: 20 }}>จุดในครัว · Kitchen stations</h2>
            <p style={{ margin: 0, fontSize: 14 }}>แต่ละเมนูเลือกจุดได้ จอครัวกรองตามจุด และเครื่องพิมพ์จะแยกตามจุด · Each dish goes to a station; screens (and later printers) filter by it.</p>
            {stations.map((st, i) => (
              <div key={st.id} className="row" style={{ gap: 6 }}>
                <input aria-label="Station name (Thai)" value={st.name.th ?? ''} placeholder="กระทะ" onChange={(e) => setStations(stations.map((x, j) => (j === i ? { ...x, name: { ...x.name, th: e.target.value } } : x)))} style={{ flex: 1, minWidth: 0, border: 'var(--line)', borderRadius: 10, padding: 8, font: 'inherit' }} />
                <input aria-label="Station name (English)" value={st.name.en ?? ''} placeholder="Wok" onChange={(e) => setStations(stations.map((x, j) => (j === i ? { ...x, name: { ...x.name, en: e.target.value } } : x)))} style={{ flex: 1, minWidth: 0, border: 'var(--line)', borderRadius: 10, padding: 8, font: 'inherit' }} />
                <button className="icon-btn" aria-label={`Remove ${main(st.name)}`} disabled={stations.length === 1} onClick={() => setStations(stations.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            <button className="ghost" style={{ color: 'var(--ink)', borderColor: 'var(--ink)' }} onClick={() => setStations([...stations, { id: `s${Date.now().toString(36)}`, name: {} }])}>+ เพิ่มจุด · Add station</button>
            <button className="cta light" onClick={() => void save({ stations })}>บันทึก · Save stations</button>
          </section>

          <section className="kt" style={{ gap: 10 }}>
            <h2 className="display" style={{ margin: 0, fontSize: 20 }}>สั่งกลับบ้านออนไลน์ · Online takeout</h2>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 700 }}>
              <input type="checkbox" checked={settings.takeoutEnabled} onChange={(e) => void save({ takeoutEnabled: e.target.checked })} /> เปิดรับ · Accept takeout orders from the link
            </label>
            <p style={{ margin: 0, fontSize: 14 }}>ลูกค้าสั่งล่วงหน้า พนักงานกดรับก่อนเข้าครัว จ่ายตอนรับ · Customers order ahead; staff accept each order before it reaches the kitchen; pay at pickup.</p>
            {link && (
              <>
                <div className="qr" style={{ border: '3px solid var(--ink)', alignSelf: 'center' }} role="img" aria-label="Takeout QR" dangerouslySetInnerHTML={{ __html: link.svg }} />
                <input readOnly value={link.url} onFocus={(e) => e.target.select()} style={{ border: '2px solid var(--ink)', borderRadius: 10, padding: 8, font: 'inherit', fontSize: 12 }} aria-label="Takeout link" />
              </>
            )}
          </section>

          <section className="kt" style={{ gap: 10 }}>
            <h2 className="display" style={{ margin: 0, fontSize: 20 }}>เปิดโต๊ะเอง · Self-seating</h2>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 700 }}>
              <input type="checkbox" checked={settings.autoOpenSessions} onChange={(e) => void save({ autoOpenSessions: e.target.checked })} /> สแกน QR ที่โต๊ะแล้วเปิดโต๊ะได้เลย · Scanning a table QR opens the table
            </label>
            <p style={{ margin: 0, fontSize: 14 }}>ปิดไว้ปลอดภัยกว่า: พนักงานเปิดโต๊ะเอง · Off is safer: staff seat guests, so a photo of a table QR can't place orders.</p>
          </section>
        </div>
      )}
    </Shell>
  );
}
